import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Button, Space, Row, Spinner } from "@masterteam/mt-design-react";

import Table from "../NewTable";
import DeleteModal from "../DeleteModal";
import RiskForm from "../AddNewInitiative/Risks/RiskForm";

import api from "../../providers/api";
import useIsAr from "../../Hooks/useIsAr";
import { addToast, getLookupName, formatDate } from "../../helpers";

import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete.svg";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

const InitiativeRisks = ({ data }) => {
  const intl = useIntl();
  const [isAr] = useIsAr();

  const [risks, setRisks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [formModal, setFormModal] = useState(initialModalState);
  const [lists, setLists] = useState({
    users: [],
  });

  const [RiskLikelihood, RiskImpact, RiskCategory, RiskStatus, currentUser] =
    useSelector(({ theme, user }) => [
      theme.RiskLikelihood.item.Items,
      theme.RiskImpact.item.Items,
      theme.RiskCategory.item.Items,
      theme.RiskStatus.item.Items,
      user.user,
    ]);

  const isReadOnly = currentUser?.UserName !== data?.ManagerModel?.UserName;

  const getInitiativeRisks = async () => {
    setLoading(true);
    try {
      const res = await api.initiatives.getRisksById(data.ID);
      setRisks(res.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getUsers = () => {
    api.user
      .getAll()
      .then((res) => {
        const { StatusCode, Data } = res;
        if (StatusCode === "Success") {
          setLists({ users: Data });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      });
  };

  const handleDelete = (id) => {
    setDeleteLoading(true);
    api.initiatives
      .deleteRisk(id)
      .then((res) => {
        const { StatusCode, StatusMessage } = res;
        if (StatusCode === "Success") {
          getInitiativeRisks();
          setDeleteModal(initialModalState);
          addToast.success("toast.successDelete");
        } else if (StatusCode === "fail") {
          addToast.error(StatusMessage, {
            toastId: "get-users-error",
          });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  useEffect(() => {
    getInitiativeRisks();
    getUsers();
  }, []);

  const columns = [
    {
      key: "ID",
      cell: (item) => risks?.findIndex((x) => x.ID === item?.ID) + 1,
      header: intl.formatMessage({ id: "risk.code" }),
      width: "5%",
    },
    {
      key: "RiskName",
      cell: (item) => (isAr ? item?.ArabicName : item?.EnglishName),
      header: intl.formatMessage({ id: "common.risk-name" }),
      width: "20%",
    },
    {
      key: "Probability",
      cell: (item) =>
        getLookupName(RiskLikelihood, `${item?.Likelihood}`, isAr),
      header: intl.formatMessage({ id: "risk.probability" }),
      width: "12.5%",
    },
    {
      key: "Impact",
      cell: (item) => getLookupName(RiskImpact, `${item?.Impact}`, isAr),
      header: intl.formatMessage({ id: "risk.impact" }),
      width: "12.5%",
    },
    {
      key: "Classification",
      cell: (item) => getLookupName(RiskCategory, `${item?.Category}`, isAr),
      header: intl.formatMessage({ id: "risk.category" }),
      width: "12.5%",
    },
    {
      key: "DueDate",
      cell: (item) => formatDate(item?.DueDate),
      header: intl.formatMessage({ id: "common.end-date" }),
      width: "12.5%",
    },
    {
      key: "MitigationPlan",
      cell: (item) => item.MitigationPlan,
      header: intl.formatMessage({ id: "risk.mitigation-plan" }),
      width: "20%",
    },
    {
      key: "Status",
      cell: (item) => getLookupName(RiskStatus, `${item?.Status}`, isAr),
      header: intl.formatMessage({ id: "common.status" }),
      width: "5%",
    },
  ];

  const actions = [
    {
      key: "edit",
      variant: "primary",
      Icon: EditIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.update" }),
      onClick: (item) => {
        setFormModal({ isOpen: true, data: item });
      },
    },
    {
      key: "delete",
      variant: "error",
      Icon: DeleteIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.delete" }),
      onClick: (item) => {
        setDeleteModal({ isOpen: true, data: item.ID });
      },
    },
  ];

  return (
    <Space className="initiative-risks" direction="vertical">
      <Row align="center" justify="between">
        <div className="title-wrapper">
          {intl.formatMessage({
            id: "initiative.risks",
          })}
        </div>

        {!isReadOnly && (
          <Button
            shape="round"
            onClick={() => setFormModal({ isOpen: true, data: null })}
          >
            {intl.formatMessage({ id: "common.add" })}
          </Button>
        )}
      </Row>

      {loading ? (
        <Row justify="center">
          <Spinner size="large" />
        </Row>
      ) : (
        <Table
          data={risks}
          columns={columns}
          actions={isReadOnly ? [] : actions}
          headerHasBackgroundColor
        />
      )}

      {formModal.isOpen && (
        <RiskForm
          data={formModal.data}
          risks={risks}
          setRisks={setRisks}
          closeModal={() => setFormModal(initialModalState)}
          lists={lists}
          initiativeData={data}
          getData={getInitiativeRisks}
        />
      )}

      {deleteModal.isOpen ? (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={intl.formatMessage({
            id: "initiative.risk",
          })}
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => handleDelete(deleteModal.data)}
          ButtonStatus={deleteLoading}
        />
      ) : null}
    </Space>
  );
};

export default InitiativeRisks;
